// MemberDetail.js - Component for displaying detailed member information
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { 
  Grid, 
  Typography, 
  Button, 
  Box, 
  Chip, 
  Avatar,
  Card,
  CardContent,
  Divider,
  IconButton,
  Modal,
  Paper,
  Tooltip,
  Link,
  styled
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { getAvatarUrl, getAvatarFullUrl } from '../../../../api/utils';
import noAvatar from '../../../../assets/images/no-avatar.png';

// Icons
import EmailIcon from '@mui/icons-material/Email';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import WorkIcon from '@mui/icons-material/Work';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import GitHubIcon from '@mui/icons-material/GitHub';
import LanguageIcon from '@mui/icons-material/Language';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import WarningIcon from '@mui/icons-material/Warning';
import BlockIcon from '@mui/icons-material/Block';

// Styled components
const ProfileContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  position: 'relative'
}));

const ProfileAvatar = styled(Avatar)(({ theme }) => ({
  width: 150,
  height: 150,
  margin: '0 auto',
  border: `4px solid ${theme.palette.background.paper}`,
  boxShadow: theme.shadows[3],
  cursor: 'pointer',
  transition: 'transform 0.2s ease-in-out',
  '&:hover': {
    transform: 'scale(1.05)'
  }
}));

const StatusChip = styled(Chip)(({ theme, status }) => {
  let chipColor;
  let bgColor;
  
  switch(status) {
    case 'AC':
      chipColor = theme.palette.success.main;
      bgColor = theme.palette.success.light;
      break;
    case 'PP':
      chipColor = theme.palette.warning.main;
      bgColor = theme.palette.warning.light;
      break;
    case 'IN':
      chipColor = theme.palette.error.main;
      bgColor = theme.palette.error.light;
      break;
    default:
      chipColor = theme.palette.grey[500];
      bgColor = theme.palette.grey[200];
  }
  
  return {
    backgroundColor: bgColor,
    color: chipColor,
    fontWeight: 600,
    '& .MuiChip-icon': {
      color: chipColor
    }
  };
});

const RoleChip = styled(Chip)(({ theme, role }) => {
  let chipColor;
  let bgColor;
  
  switch(role) {
    case 'AD':
      chipColor = theme.palette.primary.main;
      bgColor = theme.palette.primary.light;
      break;
    case 'FO':
      chipColor = theme.palette.secondary.main;
      bgColor = theme.palette.secondary.light;
      break;
    case 'MO':
      chipColor = theme.palette.info.main;
      bgColor = theme.palette.info.light;
      break;
    default:
      chipColor = theme.palette.grey[700];
      bgColor = theme.palette.grey[200];
  }
  
  return {
    backgroundColor: bgColor,
    color: chipColor,
    fontWeight: 600,
    '& .MuiChip-icon': {
      color: chipColor
    }
  };
});

const SocialButton = styled(Button)(({ theme }) => ({
  marginRight: theme.spacing(1),
  marginBottom: theme.spacing(1),
  borderRadius: theme.shape.borderRadius,
  textTransform: 'none'
}));

const InfoCard = styled(Card)(({ theme }) => ({
  height: '100%',
  borderRadius: theme.shape.borderRadius * 1.5,
  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: theme.shadows[3]
  }
}));

const MemberDetail = ({ member, role, validateAccount }) => {
  const { t } = useTranslation();
  const [openImage, setOpenImage] = useState(false);
  const [showValidationButtons, setShowValidationButtons] = useState(
    role === 'admin' && member?.status === 'PP'
  );
  
  // Handle image loading error
  const handleImageRenderError = (e) => {
    e.target.src = noAvatar;
  };
  
  // Validate URL format
  function isValidURL(url) {
    if (!url) return false;
    const pattern = /^((https?|ftp):\/\/)?[\w-]+(\.[\w-]+)+([\w.,@?^=%&:/~+#-]*[\w@?^=%&/~+#-])?$/;
    return pattern.test(url);
  }
  
  // Open website in new tab
  function openWebsite(url) {
    if (!url) return;
    
    let formattedUrl = url;
    if (isValidURL(url) && !url.startsWith('http://') && !url.startsWith('https://')) {
      formattedUrl = 'https://' + url;
    }
    
    window.open(formattedUrl, '_blank');
  }
  
  // Handle account validation
  const handleAccountValidation = (result) => {
    validateAccount(result);
    setShowValidationButtons(false);
  };
  
  // Get appropriate icon for social links
  const getLinkIcon = (url) => {
    if (!url) return <LanguageIcon />;
    
    if (url.includes('linkedin.com')) {
      return <LinkedInIcon />;
    } else if (url.includes('github.com')) {
      return <GitHubIcon />;
    } else {
      return <LanguageIcon />;
    }
  };
  
  // Get status icon based on status
  const getStatusIcon = (status) => {
    switch(status) {
      case 'AC':
        return <VerifiedUserIcon />;
      case 'PP':
        return <WarningIcon />;
      case 'IN':
        return <BlockIcon />;
      default:
        return null;
    }
  };
  
  // Get status label based on status code
  const getStatusLabel = (status) => {
    switch(status) {
      case 'AC':
        return 'Active';
      case 'PP':
        return 'Pending Approval';
      case 'IN':
        return 'Inactive';
      default:
        return 'Unknown';
    }
  };
  
  // Get role label based on role code
  const getRoleLabel = (roleCode) => {
    switch(roleCode) {
      case 'AD':
        return 'Administrator';
      case 'FO':
        return 'Founder';
      case 'MO':
        return 'Moderator';
      case 'PA':
        return 'Participant';
      case 'GU':
        return 'Guest';
      default:
        return 'Member';
    }
  };
  
  if (!member || !member.user) {
    return (
      <Box sx={{ textAlign: 'center', p: 4 }}>
        <Typography variant="h6">Member data not available</Typography>
      </Box>
    );
  }
  
  const { user, status, role: memberRole } = member;
  
  return (
    <ProfileContainer>
      {/* Approval buttons for admins when member status is pending */}
      {showValidationButtons && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <Button
            variant="contained"
            color="success"
            startIcon={<CheckCircleIcon />}
            onClick={() => handleAccountValidation(true)}
            sx={{ mr: 1 }}
          >
            Approve
          </Button>
          <Button
            variant="contained"
            color="error"
            startIcon={<CancelIcon />}
            onClick={() => handleAccountValidation(false)}
          >
            Reject
          </Button>
        </Box>
      )}
      
      <Grid container spacing={3}>
        {/* Profile Section - Left Column */}
        <Grid item xs={12} md={4}>
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <ProfileAvatar
              src={getAvatarUrl(user, noAvatar)}
              alt={`${user.first_name} ${user.last_name}`}
              onClick={() => setOpenImage(true)}
              onError={handleImageRenderError}
            />
            
            <Typography variant="h5" sx={{ mt: 2, fontWeight: 600 }}>
              {user.first_name} {user.last_name}
            </Typography>
            
            {user.alias && (
              <Typography variant="subtitle1" color="text.secondary" gutterBottom>
                {user.alias}
              </Typography>
            )}
            
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1, mb: 2 }}>
              <StatusChip 
                icon={getStatusIcon(status)}
                label={getStatusLabel(status)}
                status={status}
                sx={{ mr: 1 }}
              />
              
              <RoleChip 
                label={getRoleLabel(memberRole)}
                role={memberRole}
              />
            </Box>
            
            {/* Social links/contact buttons */}
            <Box sx={{ mt: 3 }}>
              {user.link_1 && (
                <SocialButton
                  variant="contained"
                  color="primary"
                  startIcon={getLinkIcon(user.link_1)}
                  endIcon={<OpenInNewIcon />}
                  onClick={() => openWebsite(user.link_1)}
                  fullWidth
                  sx={{ mb: 2 }}
                >
                  Contact Me
                </SocialButton>
              )}
              
              {user.link_2 && (
                <SocialButton
                  variant="outlined"
                  color="primary"
                  startIcon={getLinkIcon(user.link_2)}
                  endIcon={<OpenInNewIcon />}
                  onClick={() => openWebsite(user.link_2)}
                  fullWidth
                >
                  What I Do
                </SocialButton>
              )}
              
              {user.email && (
                <Tooltip title={`Email: ${user.email}`}>
                  <IconButton 
                    color="primary" 
                    component={Link} 
                    href={`mailto:${user.email}`}
                    sx={{ mt: 1 }}
                  >
                    <EmailIcon />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          </Box>
          
          {/* Tags */}
          {user.tags_offer && user.tags_offer.length > 0 && (
            <InfoCard elevation={1}>
              <CardContent>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  Skills & Interests
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}>
                  {user.tags_offer.map((tag) => (
                    <Chip
                      key={tag.name}
                      label={tag.name}
                      size="small"
                      sx={{ 
                        bgcolor: 'rgba(0, 0, 0, 0.04)', 
                        '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.08)' } 
                      }}
                    />
                  ))}
                </Box>
              </CardContent>
            </InfoCard>
          )}
        </Grid>
        
        {/* Profile Info - Right Column */}
        <Grid item xs={12} md={8}>
          <Grid container spacing={3}>
            {/* Bio */}
            <Grid item xs={12}>
              <InfoCard elevation={1}>
                <CardContent>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    About
                  </Typography>
                  <Typography variant="body1" sx={{ mt: 1, whiteSpace: 'pre-line' }}>
                    {user.description || "No bio information provided."}
                  </Typography>
                </CardContent>
              </InfoCard>
            </Grid>
            
            {/* Contact Information */}
            {(user.email || user.location || user.company) && (
              <Grid item xs={12}>
                <InfoCard elevation={1}>
                  <CardContent>
                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                      Contact Information
                    </Typography>
                    <Divider sx={{ my: 1 }} />
                    
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                      {user.email && (
                        <Grid item xs={12} sm={6}>
                          <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                            <EmailIcon color="action" sx={{ mt: 0.5, mr: 1 }} />
                            <Box>
                              <Typography variant="body2" color="text.secondary">
                                Email
                              </Typography>
                              <Typography variant="body1">
                                <Link href={`mailto:${user.email}`} underline="hover">
                                  {user.email}
                                </Link>
                              </Typography>
                            </Box>
                          </Box>
                        </Grid>
                      )}
                      
                      {user.location && (
                        <Grid item xs={12} sm={6}>
                          <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                            <LocationOnIcon color="action" sx={{ mt: 0.5, mr: 1 }} />
                            <Box>
                              <Typography variant="body2" color="text.secondary">
                                Location
                              </Typography>
                              <Typography variant="body1">
                                {user.location}
                              </Typography>
                            </Box>
                          </Box>
                        </Grid>
                      )}
                      
                      {user.company && (
                        <Grid item xs={12} sm={6}>
                          <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                            <WorkIcon color="action" sx={{ mt: 0.5, mr: 1 }} />
                            <Box>
                              <Typography variant="body2" color="text.secondary">
                                Company
                              </Typography>
                              <Typography variant="body1">
                                {user.company}
                              </Typography>
                            </Box>
                          </Box>
                        </Grid>
                      )}
                    </Grid>
                  </CardContent>
                </InfoCard>
              </Grid>
            )}
            
            {/* Membership Information */}
            <Grid item xs={12}>
              <InfoCard elevation={1}>
                <CardContent>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    Membership Information
                  </Typography>
                  <Divider sx={{ my: 1 }} />
                  
                  <Grid container spacing={2} sx={{ mt: 1 }}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">
                        Member Since
                      </Typography>
                      <Typography variant="body1">
                        {member.created_at 
                          ? new Date(member.created_at).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric'
                            })
                          : 'Unknown'
                        }
                      </Typography>
                    </Grid>
                    
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">
                        Status
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <StatusChip 
                          size="small"
                          icon={getStatusIcon(status)}
                          label={getStatusLabel(status)}
                          status={status}
                        />
                      </Box>
                    </Grid>
                    
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">
                        Role
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <RoleChip 
                          size="small"
                          label={getRoleLabel(memberRole)}
                          role={memberRole}
                        />
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </InfoCard>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      
      {/* Modal for enlarged image */}
      <Modal
        open={openImage}
        onClose={() => setOpenImage(false)}
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      >
        <Paper 
          sx={{
            position: 'relative',
            width: { xs: '90%', sm: '70%', md: '50%' },
            maxHeight: '90vh',
            p: 2,
            outline: 'none',
            overflow: 'hidden',
            borderRadius: 2
          }}
        >
          <IconButton
            onClick={() => setOpenImage(false)}
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              color: 'white',
              bgcolor: 'rgba(0, 0, 0, 0.5)',
              '&:hover': {
                bgcolor: 'rgba(0, 0, 0, 0.7)',
              }
            }}
          >
            <CloseIcon />
          </IconButton>
          <Box
            component="img"
            src={getAvatarFullUrl(user, noAvatar)}
            alt={`${user.first_name} ${user.last_name}`}
            onError={handleImageRenderError}
            sx={{
              width: '100%',
              maxHeight: '80vh',
              objectFit: 'contain',
              display: 'block'
            }}
          />
        </Paper>
      </Modal>
    </ProfileContainer>
  );
};

MemberDetail.propTypes = {
  member: PropTypes.object,
  role: PropTypes.string,
  validateAccount: PropTypes.func,
  goBack: PropTypes.func
};

export default MemberDetail;